// src/pages/CreatorsRosterPage.jsx
import { Link } from "react-router-dom";
import { FaTiktok, FaTwitch, FaYoutube, FaTwitter, FaInstagram } from "react-icons/fa";
import playersData from "../data/playersData";

import PetersonImg from "../assets/players/peterson.jpg";
import RvssianImg from "../assets/players/Rvssian.jpg";
import LiquidImg from "../assets/players/Liquid.jpg";
import SlayImg from "../assets/players/Slay.jpg";
import ChrisImg from "../assets/players/ChrisWz.jpg";
import GxldenImg from "../assets/players/Gxlden.jpg";
import JrwydImg from "../assets/players/Jrwyd.jpg";
import PotatoImg from "../assets/players/Chips.jpg";
import ShifuImg from "../assets/players/Shifu.jpg";
import SkrmxxImg from "../assets/players/Skrmxx.jpg";
import AnnImg from "../assets/players/Ann.jpeg";
import SpltzImg from "../assets/players/Spltz.jpeg";
import LemoImg from "../assets/players/Lemo.jpg";
import DuckyImg from "../assets/players/Ducky.jpg";

const imageMap = {
  XoPeterson: PetersonImg,
  DemonRvssian: RvssianImg,
  liquid: LiquidImg,
  Slay: SlayImg,
  ChrisWZ: ChrisImg,
  yyGxlden: GxldenImg,
  Jrwyd: JrwydImg,
  Potaatochips: PotatoImg,
  Shifu: ShifuImg,
  "The Jamaican Shifu": ShifuImg,
  Skrmxx: SkrmxxImg,
  Ann: AnnImg,
  SpltzFps: SpltzImg,
  Lemo: LemoImg,
  Ducky: DuckyImg,
};

// Pinned order: Lemo first, then Partners, then the rest
const PINNED_ORDER = ["Lemo", "JamaicanShifu", "ChrisWZ", "Potaatochips"];
// These players appear here alongside their primary division
const EXTRA_CREATOR_IDS = ["Rvssian", "Peterson", "Skrmxx"];

function CreatorsRosterPage() {
  const pinned = PINNED_ORDER.map((id) => playersData.find((p) => p.id === id)).filter(Boolean);
  const rest = playersData.filter(
    (p) => p.division === "Content Creators" && !PINNED_ORDER.includes(p.id)
  );
  const extras = playersData.filter((p) => EXTRA_CREATOR_IDS.includes(p.id));
  const creators = [...pinned, ...rest, ...extras];

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-red-950/40 to-black">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(rgba(220,38,38,0.3) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(220,38,38,0.3) 1px, transparent 1px)`,
              backgroundSize: "30px 30px",
            }}
          ></div>
        </div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-600/20 blur-[128px] rounded-full"></div>
        <div className="relative z-10 text-center px-6">
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-2 block">
            Content & Community
          </span>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-4">
            <span className="text-red-600">Creators</span> Roster
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            The voices, faces, and creators representing Prime Valor across every platform
          </p>
          <Link
            to="/roster"
            className="inline-flex items-center gap-2 mt-6 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Teams
          </Link>
        </div>
      </section>

      {/* Creators Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 uppercase border-l-4 border-red-600 pl-4">
            Content Creators
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {creators.map((player) => (
              <div
                key={player.id}
                className="group bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-red-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/20"
              >
                {/* Player Image — links to profile */}
                <Link to={`/roster/${player.id}`} className="block aspect-square overflow-hidden bg-gray-800">
                  {imageMap[player.name] ? (
                    <img
                      src={imageMap[player.name]}
                      alt={player.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                      <span className="text-4xl font-black text-red-600/30">
                        {player.name.charAt(0)}
                      </span>
                    </div>
                  )}
                </Link>

                {/* Info */}
                <div className="p-4">
                  <span className="text-red-500 text-xs font-semibold uppercase tracking-wider">
                    {player.title || "Content Creator"}
                  </span>
                  <Link to={`/roster/${player.id}`} className="block">
                    <h3 className="text-white font-bold text-lg mt-1 group-hover:text-red-400 transition-colors">
                      {player.name}
                    </h3>
                  </Link>
                  <p className="text-gray-500 text-sm mt-1">{player.country}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {player.socials?.tiktok && (
                      <a href={player.socials.tiktok} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-red-500 transition-colors">
                        <FaTiktok size={14} />
                      </a>
                    )}
                    {player.socials?.twitch && (
                      <a href={player.socials.twitch} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-500 transition-colors">
                        <FaTwitch size={14} />
                      </a>
                    )}
                    {player.socials?.youtube && (
                      <a href={player.socials.youtube} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-red-600 transition-colors">
                        <FaYoutube size={14} />
                      </a>
                    )}
                    {player.socials?.twitter && (
                      <a href={player.socials.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-400 transition-colors">
                        <FaTwitter size={14} />
                      </a>
                    )}
                    {player.socials?.instagram && (
                      <a href={player.socials.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-pink-500 transition-colors">
                        <FaInstagram size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default CreatorsRosterPage;
